import { createContext, useContext, useCallback, useMemo, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  useOnboardingProgress,
  useCompleteWizard,
  useSkipWizard,
  useUpdateWizardStep,
  useSetTeamType,
  useCompleteChecklistItem,
  useDismissChecklist,
  useMarkTooltipSeen,
  useDisableTooltips,
} from '@/hooks/useOnboarding';
import {
  OnboardingProgress,
  OnboardingUserType,
  OnboardingVariant,
  AnyChecklistItemId,
  CHECKLIST_ITEMS,
  CLIENT_CHECKLIST_ITEMS,
  AGENCY_CHECKLIST_ITEMS,
} from '@/services/onboarding.service';

/**
 * Onboarding Context
 *
 * Global state for onboarding UI:
 * - Welcome wizard visibility and state
 * - Checklist progress and completion
 * - Tooltip visibility and tracking
 *
 * Provides methods to interact with onboarding state:
 * - Complete/skip wizard
 * - Mark checklist items complete
 * - Track tooltip views
 */

interface OnboardingContextValue {
  // State
  progress: OnboardingProgress | undefined;
  isLoading: boolean;
  variant: OnboardingVariant;
  showWizard: boolean;
  showChecklist: boolean;
  checklistProgress: number;
  seenTooltips: string[];
  tooltipsEnabled: boolean;

  // Wizard actions
  completeWizard: () => Promise<void>;
  skipWizard: () => Promise<void>;
  updateWizardStep: (step: number) => Promise<void>;
  setTeamType: (teamType: OnboardingUserType) => Promise<void>;

  // Checklist actions
  completeChecklistItem: (item: AnyChecklistItemId) => Promise<void>;
  dismissChecklist: () => Promise<void>;
  refetchProgress: () => void;

  // Tooltip actions
  markTooltipSeen: (tooltipId: string) => Promise<void>;
  disableTooltips: () => Promise<void>;
  isTooltipSeen: (tooltipId: string) => boolean;

  // Constants
  CHECKLIST_ITEMS: typeof CHECKLIST_ITEMS;
  CLIENT_CHECKLIST_ITEMS: typeof CLIENT_CHECKLIST_ITEMS;
  AGENCY_CHECKLIST_ITEMS: typeof AGENCY_CHECKLIST_ITEMS;
}

const OnboardingContext = createContext<OnboardingContextValue | null>(null);

interface OnboardingProviderProps {
  children: ReactNode;
}

export function OnboardingProvider({ children }: OnboardingProviderProps) {
  const { user, isAuthenticated, isLoading: isAuthLoading } = useAuth();

  // Product rule: while the user still has to pick a plan (needsPlan on /me),
  // the welcome wizard and checklist stay hidden. This is purely derived from
  // the auth user: nothing is persisted or sent to the server, so the wizard
  // opens naturally once /me is refetched with needsPlan=false.
  const holdForPlan = user?.needsPlan === true;

  // Only fetch onboarding data once the auth bootstrap has FULLY settled.
  // `isAuthenticated` alone is insufficient: on a full page reload this query
  // can fire during AuthContext's init window, before the access token is set
  // (isAuthenticated flips true off a stored user before the token exists).
  // A tokenless request here 401s ("No token provided"), which triggers an
  // extra token refresh — under a transient network blip during bootstrap
  // this becomes a silent refresh<->401 loop that never reaches a stable
  // authenticated state. `isAuthLoading` is true for the whole bootstrap and
  // only flips false in AuthContext init's `finally`, after the token+user
  // are set (or init has failed), so gating on `!isAuthLoading` guarantees
  // the token exists before this query is allowed to fire.
  //
  // `authReady` is also used to guard the imperative `refetchProgress()`
  // below: React Query's `refetch()` BYPASSES the `enabled` flag entirely,
  // and consumers (e.g. FloatingChecklist/DashboardChecklist, mounted
  // app-wide) call `refetchProgress()` from a mount effect. Without this
  // guard that imperative call is what actually fires onboarding during the
  // bootstrap window, tokenless-401ing and re-triggering the same refresh
  // churn the `enabled` gate was meant to prevent.
  const authReady = isAuthenticated && !isAuthLoading;
  const { data: progress, isLoading, refetch } = useOnboardingProgress(authReady);

  // Destructure mutateAsync for stable callback references.
  // Depending on the whole mutation object causes infinite loops because
  // mutation state (isLoading, etc.) changes on every call, creating new
  // callback refs → new context value → consumer re-renders → useEffect re-fires.
  const { mutateAsync: completeWizardAsync } = useCompleteWizard();
  const { mutateAsync: skipWizardAsync } = useSkipWizard();
  const { mutateAsync: updateWizardStepAsync } = useUpdateWizardStep();
  const { mutateAsync: setTeamTypeAsync } = useSetTeamType();
  const { mutateAsync: completeChecklistItemAsync } = useCompleteChecklistItem({ silent: true });
  const { mutateAsync: dismissChecklistAsync } = useDismissChecklist();
  const { mutateAsync: markTooltipSeenAsync } = useMarkTooltipSeen();
  const { mutateAsync: disableTooltipsAsync } = useDisableTooltips();

  const completeWizard = useCallback(async () => {
    await completeWizardAsync();
  }, [completeWizardAsync]);

  const skipWizard = useCallback(async () => {
    await skipWizardAsync();
  }, [skipWizardAsync]);

  const updateWizardStep = useCallback(async (step: number) => {
    await updateWizardStepAsync(step);
  }, [updateWizardStepAsync]);

  const setTeamType = useCallback(async (teamType: OnboardingUserType) => {
    await setTeamTypeAsync(teamType);
  }, [setTeamTypeAsync]);

  const completeChecklistItem = useCallback(async (item: AnyChecklistItemId) => {
    await completeChecklistItemAsync(item);
  }, [completeChecklistItemAsync]);

  const dismissChecklist = useCallback(async () => {
    await dismissChecklistAsync();
  }, [dismissChecklistAsync]);

  const refetchProgress = useCallback(() => {
    // `refetch()` ignores the `enabled` flag, so this guard is what actually
    // stops onboarding from firing tokenless during the bootstrap window
    // (see `authReady` comment above).
    if (!authReady) return;
    void refetch();
  }, [refetch, authReady]);

  const markTooltipSeen = useCallback(async (tooltipId: string) => {
    await markTooltipSeenAsync(tooltipId);
  }, [markTooltipSeenAsync]);

  const disableTooltips = useCallback(async () => {
    await disableTooltipsAsync();
  }, [disableTooltipsAsync]);

  const isTooltipSeen = useCallback((tooltipId: string) => {
    return progress?.seenTooltips?.includes(tooltipId) ?? false;
  }, [progress?.seenTooltips]);

  const value = useMemo<OnboardingContextValue>(() => ({
    progress,
    isLoading,
    variant: progress?.variant ?? 'regular',
    showWizard: !holdForPlan && (progress?.showWizard ?? false),
    showChecklist: !holdForPlan && (progress?.showChecklist ?? false),
    checklistProgress: progress?.checklistProgress ?? 0,
    seenTooltips: progress?.seenTooltips ?? [],
    tooltipsEnabled: !(progress?.tooltipsDisabled ?? false),

    completeWizard,
    skipWizard,
    updateWizardStep,
    setTeamType,
    completeChecklistItem,
    dismissChecklist,
    refetchProgress,
    markTooltipSeen,
    disableTooltips,
    isTooltipSeen,

    CHECKLIST_ITEMS,
    CLIENT_CHECKLIST_ITEMS,
    AGENCY_CHECKLIST_ITEMS,
  }), [
    progress,
    isLoading,
    holdForPlan,
    completeWizard,
    skipWizard,
    updateWizardStep,
    setTeamType,
    completeChecklistItem,
    dismissChecklist,
    refetchProgress,
    markTooltipSeen,
    disableTooltips,
    isTooltipSeen,
  ]);

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
}

/**
 * useOnboarding Hook
 *
 * Access onboarding context
 * @throws Error if used outside OnboardingProvider
 */
export function useOnboarding() {
  const context = useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within OnboardingProvider');
  }
  return context;
}
